import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AppStore } from 'app.store';
import { AuthInterceptor } from 'core/interceptors/auth.interceptor';
import { ErrorInterceptor } from 'core/interceptors/error.interceptor';

export enum HttpResponseCode {
  NotFound = 404,
  OK = 200,
  InternalServerError = 500,
  Unauthorized = 401,
}

export enum HttpMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export interface RequestConfig extends AxiosRequestConfig {
  retry?: number;
  retryDelay?: number;
  // retryCondition?: () => boolean;
  headers?: Record<string, string>;
  toleratedErrors?: number[];
  onToleratedError?: (status: number) => void;
  [key: string]: unknown;
}

const globalConfig: RequestConfig = {
  retry: 0,
  retryDelay: 1000,
};

abstract class HttpBase {
  protected readonly instance: AxiosInstance;
  protected readonly appContext?: AppStore;

  public constructor(appContext?: AppStore) {
    this.instance = axios.create();
    this.appContext = appContext;

    ErrorInterceptor(this.instance, appContext);
    AuthInterceptor(this.instance);
  }
}

export class HttpClient extends HttpBase {
  private requests: Map<string, Promise<AxiosResponse<unknown>>> = new Map();
  private static httpInstance: HttpClient;

  private constructor(appContext?: AppStore) {
    super(appContext);
  }

  public static initialize(appContext: AppStore) {
    // NOTE: should be run on app bootstrap
    this.httpInstance = new HttpClient(appContext);
  }

  public static get GetInstance() {
    if (!this.httpInstance) {
      this.httpInstance = new HttpClient();
    }

    return this.httpInstance;
  }

  private static RequestKey(...args: unknown[]): string {
    try {
      return JSON.stringify(args);
    } catch (err) {
      console.error('Invalid RequestKey: ', args);
      throw new Error('Invalid RequestKey');
    }
  }

  public get<T>(url: string, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }

    const config = {
      ...globalConfig,
      ...options,
      validateStatus: (status: number) => this.validateStatus(status, options),
    };
    const request = this.instance
      .get<T>(url, config)
      .then((res) => {
        this.requests.delete(key);
        return res;
      })
      .catch((error) => {
        console.error('Failed get request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public getBlob<T>(url: string, options: RequestConfig = {}, allowDuplicates = false): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance
      .get<T>(url, { ...options, responseType: 'blob' })
      .then((res) => {
        this.requests.delete(key);
        return res;
      })
      .catch((error) => {
        console.error('Failed get request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public post<K, T>(
    url: string,
    data?: K,
    options: RequestConfig = {},
    allowDuplicates = false
  ): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, data, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const config = { ...globalConfig, ...options };
    const request = this.instance
      .post<T>(url, data, config)
      .then((res) => {
        this.requests.delete(key);
        return res;
      })
      .catch((error) => {
        console.error('Failed post request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public put<K, T>(
    url: string,
    data: K,
    options: RequestConfig = {},
    allowDuplicates = false
  ): Promise<AxiosResponse<T>> {
    const key = HttpClient.RequestKey(url, data, options);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance
      .put<T>(url, data, options)
      .then((res) => {
        this.requests.delete(key);
        return res;
      })
      .catch((error) => {
        console.error('Failed put request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  public delete<K, T>(
    url: string,
    data: K,
    options: RequestConfig = {},
    allowDuplicates = false
  ): Promise<AxiosResponse<T>> {
    const requestOptions = {
      ...options,
      body: data,
    };
    const key = HttpClient.RequestKey(url, requestOptions);
    if (this.requests.has(key) && !allowDuplicates) {
      return this.requests.get(key) as Promise<AxiosResponse<T>>;
    }
    const request = this.instance
      .delete<T>(url, requestOptions)
      .then((res) => {
        this.requests.delete(key);
        return res;
      })
      .catch((error) => {
        console.error('Failed delete request -> ', error);
        this.requests.delete(key);
        return Promise.reject(error);
      });
    this.requests.set(key, request);
    return request;
  }

  private validateStatus(status: number, options: RequestConfig) {
    const tolerated = options.toleratedErrors;

    // Force validation if its tolerated status
    if (tolerated?.includes(status)) {
      if (options.onToleratedError) {
        options.onToleratedError(status);
      }

      return true;
    }

    // Default to error for 400+ status
    return status < 400;
  }
}